import { Button } from '@components/ui';
import {
  type JobPdfDetails,
  JobReportDocument,
} from '@features/buyer/jobs/single-job-detail/job-report';
import { useJobPdfQuery } from '@lib/api/jobs';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { pdf } from '@react-pdf/renderer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
};

interface ReportDownloadButtonProps {
  jobId: string;
  fileName: string;
}

export const ReportDownloadButton = ({
  jobId,
  fileName,
}: ReportDownloadButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const { data, isLoading } = useJobPdfQuery(jobId);
  const pdfData = data as JobPdfDetails | undefined;
  const [generating, setGenerating] = useState(false);

  const handleDownload = async (): Promise<void> => {
    if (!pdfData) return;
    setGenerating(true);
    try {
      const blob = await pdf(<JobReportDocument pdfData={pdfData} />).toBlob();
      downloadBlob(blob, fileName);
    } catch {
      const message: string = t('ratingDetail.reportFailed');
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Button
      variant="secondary"
      loading={isLoading || generating}
      disabled={!pdfData}
      onClick={() => void handleDownload()}
    >
      <FileDownloadOutlined aria-hidden sx={{ fontSize: 18, mr: 0.75 }} />
      {t('ratingDetail.downloadReport')}
    </Button>
  );
};
